/**
 * Admission policy for a holder of a call link.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallLinkPolicy.swift`.
 *
 * Pure decision function: takes the decoded link plus the set of room ids
 * the local store has already seen revoked, and returns whether the joiner
 * should be admitted directly, queued for approval, or refused. The
 * signalling layer for "request to join" / "admit" is a follow-up; the
 * policy here is local so a malicious server cannot downgrade an
 * approval-required link to open-mode without flipping a signed flag byte
 * (and thus invalidating the owner signature).
 *
 * Pure logic only. No IndexedDB, no SolidJS imports.
 */

import {
  type CallLink,
  CallLinkFlags,
  deriveRoomId,
  verifyCallLink,
} from "./CallLink";

// ---------------------------------------------------------------------------
// Decision type
// ---------------------------------------------------------------------------

export type CallLinkAdmissionDecision =
  | { tag: "admitDirectly" }
  | { tag: "requireApproval"; approver: string }
  | { tag: "rejectRevoked" }
  | { tag: "rejectInvalid" };

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Decides what should happen when a joiner presents `link`.
 *
 *   1. If the owner signature does not verify → `rejectInvalid`.
 *   2. Else if the link's derived roomId is in `knownRevocations`
 *      → `rejectRevoked`.
 *   3. Else if `link.flags` has `approvalRequired` bit set →
 *      `requireApproval` with `approver = link.ownerUserId`.
 *   4. Else → `admitDirectly`.
 *
 * Mirrors iOS `CallLinkPolicy.evaluate` exactly.
 */
export async function evaluateCallLinkPolicy(
  link: CallLink,
  knownRevocations: ReadonlySet<string>,
): Promise<CallLinkAdmissionDecision> {
  const valid = await verifyCallLink(link);
  if (!valid) {
    return { tag: "rejectInvalid" };
  }

  const roomId = await deriveRoomId(link.secret);
  if (knownRevocations.has(roomId.asString())) {
    return { tag: "rejectRevoked" };
  }

  if ((link.flags & CallLinkFlags.approvalRequired) !== 0) {
    return { tag: "requireApproval", approver: link.ownerUserId };
  }

  return { tag: "admitDirectly" };
}
